// biome-ignore lint/performance/noNamespaceImport: <Need for proper Sentry work>
import * as Sentry from "@sentry/react";
import { refreshCognitoToken } from "@simble/shared/lib/cognito-refresh";
import { getCookie, removeCookie, setCookie } from "@simble/shared/lib/cookie-utils";
import logger from "@simble/shared/lib/logger";
import { safeSessionStorage } from "@simble/shared/lib/safe-storage";
import { resetStore } from "@/store/local-store";

const REFRESH_TOKEN_KEY = "refresh_token";

let refreshPromise: Promise<string | null> | null = null;

export const getRefreshToken = (): string | null => {
  const fromStorage = safeSessionStorage.getItem(REFRESH_TOKEN_KEY);
  if (fromStorage) {
    return fromStorage;
  }
  return getCookie(REFRESH_TOKEN_KEY);
};

export const setRefreshToken = (value: string): void => {
  setCookie(REFRESH_TOKEN_KEY, value);
  safeSessionStorage.setItem(REFRESH_TOKEN_KEY, value);
};

const clearRefreshToken = (): void => {
  removeCookie(REFRESH_TOKEN_KEY);
  safeSessionStorage.removeItem(REFRESH_TOKEN_KEY);
};

const performTokenRefresh = async (): Promise<string | null> => {
  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    const clientId = import.meta.env.VITE_USER_POOL_WEB_CLIENT_ID;
    if (!clientId) {
      throw new Error("VITE_USER_POOL_WEB_CLIENT_ID is not defined");
    }

    const result = await refreshCognitoToken(refreshToken, clientId);
    if (!result) {
      return null;
    }

    setCookie("auth_token", result.idToken);
    if (result.refreshToken) {
      setRefreshToken(result.refreshToken);
    }
    return result.idToken;
  } catch (error) {
    logger.error("Token refresh failed:", error);
    Sentry.captureException(error);
    logout();
    return null;
  } finally {
    refreshPromise = null;
  }
};

export const refreshAuthToken = async (): Promise<string | null> => {
  if (refreshPromise) {
    return await refreshPromise;
  }
  refreshPromise = performTokenRefresh();
  return await refreshPromise;
};

export const logout = (): void => {
  removeCookie("auth_token");
  clearRefreshToken();
  Sentry.setUser(null);
  resetStore();
};
